import arrayMutators from "final-form-arrays";
import React, { useState } from "react";
import { Form } from "react-final-form";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Flx from "../layout/Flx";
import RffFormSpy from "./shared/RffFormSpy";

/**
 * A compact form stepper with optional linear progress bar.
 *
 * Props:
 * - initialValues: object with initial form values
 * - onSubmit: function to call with form values on final submit
 * - children: one or more step components (<YourStepComponent stepName="..." validate={...} />)
 * - loading: boolean to disable navigation during async operations
 * - sx: style overrides for the root container
 * - progressVariant: 'stepper' (default) to show step labels, or 'linear' to show a LinearProgress bar
 *
 * Example:
 * <RffFormStepper
 *   initialValues={initialValues}
 *   onSubmit={handleSubmit}
 *   loading={submitting}
 *   progressVariant="linear"
 *   sx={{ width: '100%', maxWidth: 600 }}
 * >
 *   <StepComponent stepName="Info" validate={validateInfo} />
 *   <StepComponent stepName="Confirm" validate={validateConfirm} />
 * </RffFormStepper>
 */
const RffFormStepper = ({
  initialValues,
  onSubmit,
  children,
  loading = false,
  sx,
  alternativeLabel = true,
  progressVariant = "stepper",
  submitText = "Submit",
  stepperSx,
  skippable = false,
  formSpy = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const steps = React.Children.toArray(children).filter(Boolean);
  const [step, setStep] = useState(0);
  const isLastStep = step === steps.length - 1;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={steps[step].props.validate}
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, form, submitting, pristine }) => {
        const runValidation = () => {
          form.getRegisteredFields().forEach((name) => form.blur(name));
          const errors = form.getState().errors || {};
          return Object.keys(errors).length === 0;
        };

        const handleNext = (e) => {
          e.preventDefault(); // Prevent any form submission
          e.stopPropagation(); // Stop event bubbling
          console.log("Next click");
          if (runValidation()) {
            setStep((s) => s + 1);
          }
        };

        const handleFormSubmit = (e) => {
          if (!isLastStep) {
            e.preventDefault(); // Prevent submission if not on last step
            return;
          }
          handleSubmit(e);
        };

        const linearValue = steps.length > 0 ? (step / steps.length) * 100 : 0;
        return (
          <Box
            component="form"
            onSubmit={handleFormSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              //
              // gap: 2,
              // gap: 2,
              ...sx,
            }}
          >
            {/* Progress Indicator */}
            {progressVariant === "stepper" && !isMobile ? (
              <Stepper
                activeStep={step}
                alternativeLabel={alternativeLabel}
                sx={{ mb: alternativeLabel ? 5 : 2, ...stepperSx }}
              >
                {steps.map((child, idx) => (
                  <Step
                    key={idx}
                    sx={{
                      // mb: 5,
                      ".MuiStepLabel-label": {
                        mt: alternativeLabel
                          ? "6px !important"
                          : "0px !important",
                        fontSize: "10px",
                        fontWeight: 400,
                      },

                      ".Mui-disabled .MuiSvgIcon-root": {
                        color: "#0000001f",
                      },
                    }}
                  >
                    <StepLabel>
                      {child.props.stepName ||
                        child.props.title ||
                        `Step ${idx + 1}`}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <LinearProgress
                variant="determinate"
                value={linearValue}
                sx={{ mb: 5, ...stepperSx }}
              />
            )}

            {/* Current Step Content */}
            <Box>{steps[step]}</Box>

            {/* Navigation Buttons */}
            <Flx fw jb ac wrap sx={{ mt: 5 }}>
              <Button
                size="large"
                variant="outlined"
                onClick={() => setStep((s) => s - 1)}
                type="button"
                color="inherit"
                disabled={step === 0}
                sx={{ opacity: step > 0 ? 1 : 0 }}
              >
                Back
              </Button>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                {steps[step].props?.skippable ? (
                  <Button
                    variant="outlined"
                    size="large"
                    color="inherit"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setStep((s) => s + 1);
                    }}
                    type="button"
                  >
                    Skip
                  </Button>
                ) : null}

                {!isLastStep ? (
                  <Button
                    size="large"
                    variant="contained"
                    onClick={handleNext}
                    type="button"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    loading={loading}
                    disabled={submitting || loading}
                  >
                    {submitText}
                  </Button>
                )}
              </Box>
            </Flx>
            <RffFormSpy formSpy={formSpy} />
          </Box>
        );
      }}
    </Form>
  );
};

export default RffFormStepper;
